/**
 * ClientMatterPicker ("Matter Finder")
 *
 * The universal Client and Matter picker used by EdgeMail, Billing, Calendar,
 * Meetings, DMS, Lex projects and agent delegation. One box searches matters
 * by title, number (loosely), client, parties and client email; with nothing
 * typed it shows context suggestions, the user's recents, then everything the
 * user may see. Rows carry title, number, client, practice area, court, stage,
 * responsible attorney and last activity so nobody has to remember a number.
 *
 * Data: POST /api/v1/matters/find (MatterFinderService), which applies matter
 * visibility rules and ethical walls server-side. Picks are recorded so they
 * rank higher and appear under Recent next time.
 *
 * Props are backward compatible with the previous two-step picker; hosts pass
 * `context` to get suggestions relevant to where the picker opened.
 */
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, BriefcaseIcon, UserIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { matterFinderApi } from '../../config/api';
import type { FinderClientItem, FinderContext, FinderFilters, FinderGroup, FinderMatterItem } from './matterFinder.types';
import { flattenGroups, highlightSegments, lastMatterFor, matterMetaParts, relativeActivity, rememberLastMatter, stageLabel, tokenizeFinderQuery } from './matterFinder.helpers';

// ─── Types (kept for hosts that import them) ────────────────────────────────
export interface ClientResult {
  id: string;
  name: string;
  type?: string;
}

export interface MatterResult {
  id: string;
  matter_id?: string;
  matter_number?: string;
  title?: string;
  status?: string;
  stage?: string;
  practice_area?: string;
  jurisdiction_code?: string;
  jurisdiction?: string;
  case_subtype?: string;
  case_subtype_local_label?: string;
  case_subtype_resolution_status?: string;
  responsibleAttorneyName?: string;
  responsibleStaffName?: string;
  taskListLabel?: string;
  taskTemplateId?: string;
  taskTemplateLabel?: string;
}

export interface ClientMatterSelection {
  clientId: string;
  clientName: string;
  matterId: string;
  matterNumber: string;
  matterName: string;
  status?: string | null;
  stage?: string | null;
  practiceArea?: string | null;
  practice_area?: string | null;
  jurisdictionCode?: string | null;
  jurisdiction_code?: string | null;
  jurisdiction?: string | null;
  caseSubtype?: string | null;
  case_subtype?: string | null;
  caseSubtypeLocalLabel?: string | null;
  case_subtype_local_label?: string | null;
  caseSubtypeResolutionStatus?: string | null;
  case_subtype_resolution_status?: string | null;
  responsibleAttorneyName?: string | null;
  responsibleStaffName?: string | null;
  taskListLabel?: string | null;
  taskTemplateId?: string | null;
  taskTemplateLabel?: string | null;
}

export type { FinderContext } from './matterFinder.types';

interface ClientMatterPickerProps {
  /** Called when a matter is selected (client+matter fully resolved) */
  onSelect: (selection: ClientMatterSelection) => void;
  /** Called when picker is dismissed without selection */
  onCancel: () => void;
  /** Existing linked matter to display (if any) */
  currentMatterId?: string | null;
  currentMatterLabel?: string;
  /**
   * Token layer. `em` follows the EdgeMail theme (`--em-*`); `lex` (default)
   * follows the global EdgeLex theme (`--lex-*`). `dark` and `light` are
   * accepted for older hosts and behave as `lex`: the picker never paints a
   * fixed palette, so it adopts whatever theme, accent and mode is active.
   */
  variant?: 'em' | 'lex' | 'dark' | 'light';
  /** When true, skip matter selection — return after client is picked */
  clientOnly?: boolean;
  /** Accent tone for focus/pill styling */
  accentTone?: 'accent' | 'warning';
  /** Optional container sizing classes for constrained host surfaces */
  className?: string;
  /** Where the picker opened: drives the Suggested group (sender emails, folder matter, last matter…). */
  context?: FinderContext;
  /**
   * `compact` (default): a 27rem popover for pills and toolbars.
   * `expanded`: fills its container and, when at least 36rem wide, adds a
   * preview column for the highlighted row so a long list can be confirmed
   * before linking (billing, reports, projects).
   */
  size?: 'compact' | 'expanded';
}

type ChipId = 'all' | 'active' | 'mine' | 'recent' | 'billable' | 'hearing';

const CHIPS: { id: ChipId; label: string; filters: FinderFilters }[] = [
  { id: 'all', label: 'All', filters: {} },
  { id: 'active', label: 'Active', filters: { stage: 'active' } },
  { id: 'mine', label: 'Mine', filters: { mine: true } },
  { id: 'billable', label: 'Billable', filters: { billable: true } },
  { id: 'hearing', label: 'Hearing soon', filters: { hearingWithinDays: 14 } },
];

function chipsForContext(context?: FinderContext): typeof CHIPS {
  const host = context?.host;
  if (host === 'billing') return CHIPS.filter((c) => ['billable', 'all', 'mine'].includes(c.id)).sort((a, b) => (a.id === 'billable' ? -1 : b.id === 'billable' ? 1 : 0));
  if (host === 'calendar' || host === 'meeting') return CHIPS.filter((c) => ['all', 'hearing', 'active', 'mine'].includes(c.id));
  return CHIPS.filter((c) => ['all', 'active', 'mine'].includes(c.id));
}

// ─── Theme tokens → CSS variables on the container ─────────────────────────
// Every variant resolves to live theme tokens so the picker adopts whatever
// theme, accent and mode the host is running: `em` uses EdgeMail's --em-*
// layer, everything else the global --lex-* layer. `dark` and `light` are kept
// as accepted values for older hosts but no longer paint a fixed palette.
function variantVars(variant: ClientMatterPickerProps['variant'], accentTone: ClientMatterPickerProps['accentTone']): React.CSSProperties {
  const warn = accentTone === 'warning';
  const vars: Record<string, string> =
    variant === 'em'
      ? {
          '--cmp-bg': 'var(--em-surface-card)',
          '--cmp-border': 'var(--em-border-primary)',
          '--cmp-border-soft': 'var(--em-border-subtle)',
          '--cmp-input-bg': 'var(--em-surface-app)',
          '--cmp-text': 'var(--em-text-primary)',
          '--cmp-text-2': 'var(--em-text-secondary)',
          '--cmp-text-3': 'var(--em-text-tertiary)',
          '--cmp-hover': 'var(--em-state-hover)',
          '--cmp-sel': 'var(--em-state-selected)',
          '--cmp-accent': warn ? 'var(--em-status-warning-text, var(--lex-status-warning))' : 'var(--em-accent-primary)',
          '--cmp-accent-light': warn ? 'var(--em-status-warning-bg, var(--lex-status-warning-bg))' : 'var(--em-accent-primary-light)',
          '--cmp-on-accent': 'var(--em-btn-primary-text, var(--lex-accent-on-accent, #fff))',
          '--cmp-success': 'var(--em-status-success-text, var(--lex-status-success))',
          '--cmp-success-bg': 'var(--em-status-success-bg, var(--lex-status-success-bg))',
          '--cmp-warning': 'var(--em-status-warning-text, var(--lex-status-warning))',
          '--cmp-warning-bg': 'var(--em-status-warning-bg, var(--lex-status-warning-bg))',
          '--cmp-info': 'var(--lex-status-info, var(--em-text-secondary))',
          '--cmp-info-bg': 'var(--lex-status-info-bg, var(--em-border-subtle))',
          '--cmp-neutral': 'var(--em-text-secondary)',
          '--cmp-neutral-bg': 'var(--em-border-subtle)',
        }
      : {
          '--cmp-bg': 'var(--lex-surface-card)',
          '--cmp-border': 'var(--lex-border-strong)',
          '--cmp-border-soft': 'var(--lex-border-subtle, var(--lex-border))',
          '--cmp-input-bg': 'var(--lex-surface-input, var(--lex-surface-muted))',
          '--cmp-text': 'var(--lex-text-primary)',
          '--cmp-text-2': 'var(--lex-text-secondary)',
          '--cmp-text-3': 'var(--lex-text-muted)',
          '--cmp-hover': 'var(--lex-state-hover, var(--lex-surface-hover))',
          '--cmp-sel': 'var(--lex-state-selected, var(--lex-surface-hover))',
          '--cmp-accent': warn ? 'var(--lex-status-warning)' : 'var(--lex-accent-primary, var(--lex-accent))',
          '--cmp-accent-light': warn ? 'var(--lex-status-warning-bg)' : 'var(--lex-accent-primary-soft, var(--lex-accent-subtle))',
          '--cmp-on-accent': 'var(--lex-accent-on-accent, #fff)',
          '--cmp-success': 'var(--lex-status-success)',
          '--cmp-success-bg': 'var(--lex-status-success-bg)',
          '--cmp-warning': 'var(--lex-status-warning)',
          '--cmp-warning-bg': 'var(--lex-status-warning-bg)',
          '--cmp-info': 'var(--lex-status-info)',
          '--cmp-info-bg': 'var(--lex-status-info-bg)',
          '--cmp-neutral': 'var(--lex-status-neutral, var(--lex-text-secondary))',
          '--cmp-neutral-bg': 'var(--lex-status-neutral-bg, var(--lex-surface-muted))',
        };
  return vars as React.CSSProperties;
}

const STAGE_CHIP: Record<string, string> = {
  active: 'bg-[var(--cmp-success-bg)] text-[var(--cmp-success)]',
  intake: 'bg-[var(--cmp-warning-bg)] text-[var(--cmp-warning)]',
  review: 'bg-[var(--cmp-info-bg)] text-[var(--cmp-info)]',
  approved: 'bg-[var(--cmp-info-bg)] text-[var(--cmp-info)]',
  closed: 'bg-[var(--cmp-neutral-bg)] text-[var(--cmp-neutral)]',
  rejected: 'bg-[var(--cmp-neutral-bg)] text-[var(--cmp-neutral)]',
};

function Highlight({ text, tokens }: { text: string; tokens: string[] }) {
  const segs = highlightSegments(text, tokens);
  return (
    <>
      {segs.map((s, i) => (i % 2 === 1 ? <mark key={i} className="bg-transparent font-semibold text-[var(--cmp-accent)]">{s}</mark> : <span key={i}>{s}</span>))}
    </>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────
export default function ClientMatterPicker({
  onSelect,
  onCancel,
  variant = 'lex',
  clientOnly = false,
  accentTone = 'accent',
  className = '',
  context,
  size = 'compact',
}: ClientMatterPickerProps) {
  // Hosts often build `context` inline, so a new object arrives every render.
  // Everything below keys on its content, never its identity, or the fetch
  // effect would loop (see feedback: stable hook inputs).
  const contextKey = JSON.stringify(context ?? null);
  const stableContext = useMemo<FinderContext | undefined>(() => {
    const parsed = JSON.parse(contextKey) as FinderContext | null;
    if (!parsed) return undefined;
    // Billing-style hosts get the last matter they linked here unless they supplied one.
    if (parsed.host && !parsed.lastMatterId) {
      const last = lastMatterFor(parsed.host);
      if (last) return { ...parsed, lastMatterId: last };
    }
    return parsed;
  }, [contextKey]);
  const [query, setQuery] = useState('');
  const [chip, setChip] = useState<ChipId>(() => chipsForContext(stableContext)[0].id);
  const [clientFilter, setClientFilter] = useState<FinderClientItem | null>(null);
  const [groups, setGroups] = useState<FinderGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const requestSeq = useRef(0);

  const chips = useMemo(() => chipsForContext(stableContext), [stableContext]);
  const tokens = useMemo(() => tokenizeFinderQuery(query), [query]);

  // Focus the box on mount; close on outside click or Escape.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onCancel();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onCancel]);

  // One debounced read for every state change. Client-only mode asks with the
  // same query and shows just the Clients group.
  useEffect(() => {
    const seq = ++requestSeq.current;
    const filters: FinderFilters = { ...(chips.find((c) => c.id === chip)?.filters || {}) };
    if (clientFilter) filters.clientId = clientFilter.id;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await matterFinderApi.find({ q: query, filters, context: stableContext, limit: 30 });
        if (seq !== requestSeq.current) return;
        setGroups(clientOnly ? result.groups.filter((g) => g.id === 'clients') : result.groups);
        setActive(0);
      } catch (err) {
        if (seq !== requestSeq.current) return;
        setGroups([]);
        setError((err as { message?: string })?.message || 'Could not load matters');
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, query ? 160 : 0);
    return () => window.clearTimeout(timer);
  }, [query, chip, clientFilter, stableContext, chips, clientOnly]);

  const rows = useMemo(() => flattenGroups(groups), [groups]);

  // Expanded surface: show the preview column only when there is room for it.
  const [wide, setWide] = useState(false);
  useEffect(() => {
    if (size !== 'expanded' || !containerRef.current || typeof ResizeObserver === 'undefined') return;
    const el = containerRef.current;
    const measure = () => setWide(el.getBoundingClientRect().width >= 576);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [size]);
  const showPreview = size === 'expanded' && wide;
  const activeItem = rows[active]?.item;

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-row-index="${active}"]`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [active, rows]);

  const chooseMatter = useCallback(
    (m: FinderMatterItem) => {
      void matterFinderApi.recordRecent(m.id).catch(() => undefined);
      if (stableContext?.host) rememberLastMatter(stableContext.host, m.id);
      onSelect({
        clientId: m.clientId || '',
        clientName: m.clientName,
        matterId: m.id,
        matterNumber: m.matterNumber,
        matterName: m.title,
        status: m.stage,
        stage: m.stage,
        practiceArea: m.practiceArea,
        practice_area: m.practiceArea,
        jurisdictionCode: m.jurisdictionCode,
        jurisdiction_code: m.jurisdictionCode,
        jurisdiction: null,
        caseSubtype: m.caseSubtype,
        case_subtype: m.caseSubtype,
        caseSubtypeLocalLabel: m.caseSubtypeLabel,
        case_subtype_local_label: m.caseSubtypeLabel,
        caseSubtypeResolutionStatus: null,
        case_subtype_resolution_status: null,
        responsibleAttorneyName: m.responsibleAttorneyName,
        responsibleStaffName: m.responsibleStaffName,
        taskListLabel: null,
        taskTemplateId: null,
        taskTemplateLabel: null,
      });
    },
    [onSelect, stableContext],
  );

  const chooseClient = useCallback(
    (c: FinderClientItem) => {
      if (clientOnly) {
        onSelect({ clientId: c.id, clientName: c.name, matterId: '', matterNumber: '', matterName: '' });
        return;
      }
      // Narrow to this client's matters and clear the query so all of them show.
      setClientFilter(c);
      setQuery('');
      window.setTimeout(() => inputRef.current?.focus(), 0);
    },
    [clientOnly, onSelect],
  );

  const choose = useCallback(
    (row: (typeof rows)[number] | undefined) => {
      if (!row) return;
      if (row.item.kind === 'client') chooseClient(row.item);
      else chooseMatter(row.item);
    },
    [rows, chooseClient, chooseMatter],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(rows.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[active];
      if (e.shiftKey && row?.item.kind === 'matter') {
        window.open(`/matters/${row.item.id}`, '_blank', 'noopener');
        return;
      }
      choose(row);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (query) setQuery('');
      else if (clientFilter) setClientFilter(null);
      else onCancel();
    } else if (e.key === 'Backspace' && !query && clientFilter) {
      setClientFilter(null);
    }
  };

  const placeholder = clientOnly
    ? 'Client name or email'
    : clientFilter
      ? `Search ${clientFilter.name}'s matters`
      : 'Matter, number, client, party or email';

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-label={clientOnly ? 'Find a client' : 'Find a client or matter'}
      className={`${className || (size === 'expanded' ? 'w-full max-w-[46rem]' : 'w-[min(27rem,calc(100vw-2rem))]')} rounded-xl border shadow-xl z-50 overflow-hidden flex flex-col text-[13px] bg-[var(--cmp-bg)] border-[var(--cmp-border)] text-[var(--cmp-text)]`}
      style={{ ...variantVars(variant, accentTone), maxHeight: size === 'expanded' ? 'min(34rem, calc(100vh - 6rem))' : 'min(28rem, calc(100vh - 6rem))' }}
    >
      {/* Search box */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--cmp-border-soft)]">
        <MagnifyingGlassIcon className="w-4 h-4 shrink-0 text-[var(--cmp-text-3)]" />
        {clientFilter && (
          <span className="flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap bg-[var(--cmp-accent-light)] text-[var(--cmp-accent)]">
            <UserIcon className="w-3 h-3" />
            {clientFilter.name}
            <button type="button" onClick={() => setClientFilter(null)} className="hover:opacity-70" aria-label={`Clear client ${clientFilter.name}`}>
              <XMarkIcon className="w-3 h-3" />
            </button>
          </span>
        )}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label={placeholder}
          aria-activedescendant={rows[active] ? `cmp-row-${active}` : undefined}
          className="flex-1 min-w-0 bg-transparent outline-none text-sm placeholder-[var(--cmp-text-3)] text-[var(--cmp-text)]"
          autoComplete="off"
        />
        <button type="button" onClick={onCancel} className="text-[var(--cmp-text-3)] hover:text-[var(--cmp-text)]" aria-label="Close">
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Filter chips (not in client-only mode) */}
      {!clientOnly && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-[var(--cmp-border-soft)] overflow-x-auto" role="group" aria-label="Filters">
          {chips.map((c) => {
            const on = c.id === chip;
            return (
              <button
                key={c.id}
                type="button"
                aria-pressed={on}
                onClick={() => setChip(c.id)}
                className={`whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                  on
                    ? 'border-[var(--cmp-accent)] bg-[var(--cmp-accent-light)] text-[var(--cmp-accent)] font-medium'
                    : 'border-[var(--cmp-border)] text-[var(--cmp-text-2)] hover:text-[var(--cmp-text)]'
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Results (+ preview column on the expanded surface) */}
      <div className={`flex-1 min-h-0 flex ${showPreview ? 'divide-x divide-[var(--cmp-border-soft)]' : ''}`}>
      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto py-1" role="listbox" aria-busy={loading}>
        {error && <p className="px-3 py-3 text-xs text-[var(--cmp-warning)]">{error}</p>}
        {!error && rows.length === 0 && !loading && (
          <p className="px-3 py-4 text-xs text-[var(--cmp-text-3)]">
            {query
              ? <>Nothing matches <b className="font-medium text-[var(--cmp-text-2)]">{query}</b>. Try a party name, part of a number, or the client's email.</>
              : clientOnly
                ? 'Type a client name or email.'
                : 'No matters you can see yet.'}
          </p>
        )}
        {groups.map((group, gi) => {
          if (group.items.length === 0) return null;
          const startIndex = rows.findIndex((r) => r.groupIndex === gi);
          return (
            <div key={group.id}>
              <div className="flex items-baseline justify-between gap-2 px-3 pt-2 pb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--cmp-text-3)]">{group.label}</span>
                {group.reason && <span className="truncate text-[11px] text-[var(--cmp-text-3)]">{group.reason}</span>}
              </div>
              {group.items.map((item, ii) => {
                const index = startIndex + ii;
                const selected = index === active;
                const common = `w-full text-left grid grid-cols-[30px_1fr_auto] items-center gap-2.5 px-3 py-1.5 transition-colors ${
                  selected ? 'bg-[var(--cmp-sel)] shadow-[inset_3px_0_0_var(--cmp-accent)]' : 'hover:bg-[var(--cmp-hover)]'
                }`;
                if (item.kind === 'client') {
                  return (
                    <button
                      key={`c-${item.id}`}
                      id={`cmp-row-${index}`}
                      data-row-index={index}
                      role="option"
                      aria-selected={selected}
                      type="button"
                      onMouseMove={() => setActive(index)}
                      onClick={() => chooseClient(item)}
                      className={common}
                    >
                      <span className="w-[30px] h-[30px] rounded-lg grid place-items-center bg-[var(--cmp-accent-light)] text-[var(--cmp-accent)]">
                        <UserIcon className="w-4 h-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate font-medium"><Highlight text={item.name} tokens={tokens} /></span>
                        <span className="block truncate text-xs text-[var(--cmp-text-3)]">
                          {item.email ? <><Highlight text={item.email} tokens={tokens} /> · </> : null}
                          {item.matterCount} matter{item.matterCount === 1 ? '' : 's'}
                        </span>
                      </span>
                      <span className="text-[11px] text-[var(--cmp-text-3)] whitespace-nowrap">{clientOnly ? 'select' : 'show matters →'}</span>
                    </button>
                  );
                }
                const m = item;
                const meta = matterMetaParts(m, { hideClient: !!clientFilter });
                return (
                  <button
                    key={`m-${m.id}`}
                    id={`cmp-row-${index}`}
                    data-row-index={index}
                    role="option"
                    aria-selected={selected}
                    type="button"
                    onMouseMove={() => setActive(index)}
                    onClick={() => chooseMatter(m)}
                    className={common}
                    title={`${m.matterNumber} · ${m.title}`}
                  >
                    <span className="w-[30px] h-[30px] rounded-lg grid place-items-center bg-[var(--cmp-accent-light)] text-[var(--cmp-accent)]">
                      <BriefcaseIcon className="w-4 h-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-medium"><Highlight text={m.title} tokens={tokens} /></span>
                      <span className="block truncate text-xs text-[var(--cmp-text-3)]">
                        {meta.map((part, pi) => (
                          <span key={pi}>
                            {pi > 0 && ' · '}
                            <span className={pi === 0 ? 'font-mono text-[var(--cmp-text-2)]' : undefined}><Highlight text={part} tokens={tokens} /></span>
                          </span>
                        ))}
                      </span>
                    </span>
                    <span className="flex flex-col items-end gap-0.5 text-[11px] text-[var(--cmp-text-3)] whitespace-nowrap">
                      <span className={`rounded-full px-2 py-px text-[10px] font-medium ${STAGE_CHIP[m.stage] || STAGE_CHIP.closed}`}>{stageLabel(m.stage)}</span>
                      <span>
                        {m.responsibleAttorneyName ? `${m.responsibleAttorneyName} · ` : ''}
                        {m.nextHearingAt ? `hearing ${relativeActivity(m.nextHearingAt)}` : relativeActivity(m.lastActivityAt)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}
        {loading && rows.length === 0 && <p className="px-3 py-3 text-xs text-[var(--cmp-text-3)]">Searching…</p>}
      </div>
      {showPreview && (
        <aside className="w-64 shrink-0 overflow-y-auto p-3 text-xs" aria-label="Preview">
          {!activeItem && <p className="text-[var(--cmp-text-3)]">Highlight a row to preview it.</p>}
          {activeItem?.kind === 'client' && (
            <>
              <p className="text-sm font-semibold">{activeItem.name}</p>
              <p className="mt-0.5 text-[var(--cmp-text-3)]">{[activeItem.status, activeItem.email].filter(Boolean).join(' · ')}</p>
              <p className="mt-3 text-[var(--cmp-text-2)]">{activeItem.matterCount} matter{activeItem.matterCount === 1 ? '' : 's'}. Press Enter to list them.</p>
            </>
          )}
          {activeItem?.kind === 'matter' && (
            <>
              <p className="text-sm font-semibold leading-snug">{activeItem.title}</p>
              <p className="mt-0.5 font-mono text-[var(--cmp-text-2)]">{activeItem.matterNumber}</p>
              <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
                <dt className="text-[var(--cmp-text-3)]">Client</dt><dd className="min-w-0 truncate">{activeItem.clientName || '—'}</dd>
                <dt className="text-[var(--cmp-text-3)]">Practice</dt><dd className="min-w-0">{[activeItem.practiceArea, activeItem.caseSubtypeLabel || activeItem.caseSubtype].filter(Boolean).join(' / ') || '—'}</dd>
                {activeItem.jurisdictionCode && (<><dt className="text-[var(--cmp-text-3)]">Court</dt><dd>{activeItem.jurisdictionCode}</dd></>)}
                {(activeItem.plaintiff || activeItem.defendant) && (<><dt className="text-[var(--cmp-text-3)]">Parties</dt><dd className="min-w-0">{[activeItem.plaintiff, activeItem.defendant].filter(Boolean).join(' v ')}</dd></>)}
                <dt className="text-[var(--cmp-text-3)]">Stage</dt><dd>{stageLabel(activeItem.stage)}{activeItem.isBillable ? '' : ' · not billable'}</dd>
                {activeItem.responsibleAttorneyName && (<><dt className="text-[var(--cmp-text-3)]">Attorney</dt><dd>{activeItem.responsibleAttorneyName}</dd></>)}
                {activeItem.nextHearingAt && (<><dt className="text-[var(--cmp-text-3)]">Hearing</dt><dd>{new Date(activeItem.nextHearingAt).toLocaleDateString()}</dd></>)}
                <dt className="text-[var(--cmp-text-3)]">Activity</dt><dd>{relativeActivity(activeItem.lastActivityAt)}</dd>
              </dl>
              <button
                type="button"
                onClick={() => chooseMatter(activeItem)}
                className="mt-3 w-full rounded-md px-3 py-1.5 text-xs font-semibold text-[var(--cmp-on-accent)] bg-[var(--cmp-accent)] hover:opacity-90"
              >
                Link to this matter
              </button>
            </>
          )}
        </aside>
      )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 px-3 py-1.5 border-t border-[var(--cmp-border-soft)] text-[11px] text-[var(--cmp-text-3)]">
        <span className="flex items-center gap-2">
          <kbd className="rounded border border-[var(--cmp-border)] px-1 font-mono text-[10px]">↑↓</kbd> move
          <kbd className="rounded border border-[var(--cmp-border)] px-1 font-mono text-[10px]">↵</kbd> {clientOnly ? 'select' : 'link'}
          {!clientOnly && (
            <span className="hidden sm:inline-flex items-center gap-1">
              <kbd className="rounded border border-[var(--cmp-border)] px-1 font-mono text-[10px]">⇧↵</kbd> open <ArrowTopRightOnSquareIcon className="w-3 h-3" />
            </span>
          )}
        </span>
        {loading && rows.length > 0 && <span>updating…</span>}
      </div>
    </div>
  );
}
